/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useEffect, useState } from 'react'
import { Alert, Box, CircularProgress, useTheme } from '@mui/material'
import { FormProvider, useForm, useFieldArray, type SubmitHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import DefaultModalActions from '../../../components/GenericModal/DefaultModalActions'
import { ButtonType } from '../../../components/GenericButton/styles'
import { useMutation } from '@tanstack/react-query'
import { notification } from '../../../components/Notifications'
import { type ISlot } from '../../../models/DistributionPlan'
import { type ISurveyDates } from '../../../models/AutoImports'
import FormSection from './FormFields'
import { defaultAllSurveyDates, surveyDatesSchema } from './constant'
import AutoImportManager from '../../../APIManager/AutoImportManager'
import { useSlotTaskContext } from '../../../contexts/SlotTaskContext'
import { isInstructorReleaseOnGradePostingEnabled } from '../../../consts/messages'

export interface FormTypes {
  sectionFieldType: string
  condition: string
  noOfDays: string | number
  enabled: boolean
  surveyDateType: string
  subTitle: string
}

interface FormValue {
  surveyDatesFilters: FormTypes[]

}

interface SurveyDatesPanelProps {
  handleCloseModal: () => void
  setNextTab: () => void
  slot: ISlot
  loading: boolean
  surveyDatesList: ISurveyDates[]
  setSurveyDatesList: (dates: ISurveyDates[]) => void
  isLatestDataFetching: boolean
  latestDefaultDates: ISurveyDates[]
  isReadOnly: boolean
}

const SurveyDatesPanel: React.FC<SurveyDatesPanelProps> = ({
  handleCloseModal,
  slot,
  setNextTab,
  loading,
  surveyDatesList,
  setSurveyDatesList,
  isLatestDataFetching,
  latestDefaultDates,
  isReadOnly
}): JSX.Element => {
  const { setTask } = useSlotTaskContext()
  const [isApplyingLatestDates, setIsApplyingLatestDates] = useState(false)
  const isRollingResultsEnabled = slot.isRollingResultsEnabled
  const isGradePostingReleaseEnabled = isInstructorReleaseOnGradePostingEnabled(slot.isInstructorReleaseOnGradePostingEnabled)
  const methods = useForm<any>({
    resolver: yupResolver(surveyDatesSchema),
    defaultValues: {
      surveyDatesFilters: defaultAllSurveyDates
    }
  })
  const theme = useTheme()
  const { handleSubmit, control, setValue, formState: { isDirty } } = methods
  const { fields } = useFieldArray({
    control,
    name: 'surveyDatesFilters'
  })
  const { mutate, isPending } = useMutation({
    mutationFn: AutoImportManager.submitSurveyDates,
    onSuccess: (res) => {
      if (res?.status === 202) {
        const { taskId, message } = res.data ?? {}
        if (taskId) {
          setTask(taskId, message ?? 'Updating survey dates...', 'Default dates applied to all course sections.')
          notification('Updating survey dates', { variant: 'info', subTitle: 'Your changes are being applied in the background.' })
        }
        handleCloseModal()
        return
      }
      notification(res?.data?.message, { variant: 'success' })
      setNextTab()
    }
  })

  const onSubmit: SubmitHandler<FormValue> = async (values) => {
    // Guard submit in read-only mode in case form submission is triggered via keyboard.
    if (isButtonDisabled) {
      return
    }

    if (!isDirty) {
      setNextTab()
      return
    }
    const payload: Array<Partial<FormTypes>> = []

    values.surveyDatesFilters.forEach((value: FormTypes) => {
      const noOfDays = value.noOfDays || 0
      const preparedNoOfDays = value.condition === 'before' ? -noOfDays : noOfDays
      if (value.enabled) {
        payload.push({
          noOfDays: Number(preparedNoOfDays),
          sectionFieldType: value.sectionFieldType,
          surveyDateType: value.surveyDateType
        })
      }
    })
    const data = { payload, slotId: slot.slotId }
    mutate(data)
  }

  const safeSurveyDatesList = Array.isArray(surveyDatesList) ? surveyDatesList : []

  useEffect(() => {
    if (safeSurveyDatesList.length === 0) {
      return
    }

    const mappedData = safeSurveyDatesList.map((d: ISurveyDates) => {
      const noOfDays = Math.abs(d.noOfDays)

      let condition: string | number = 'on'
      if (d.noOfDays > 0) {
        condition = 'after'
      } else if (d.noOfDays < 0) {
        condition = 'before'
      }

      let subTitle = ''

      switch (d.surveyDateType) {
        case 'Course Section Survey Start Date':
          subTitle = 'The day the survey for each course section begins.'
          break
        case 'Course Section Survey End Date':
          subTitle = 'The day the survey for each course section ends.'
          break
        case 'Course Section Admin Release Date':
          subTitle = 'The default day survey results are released to admins for each course section.'
          break
        case 'Course Section Instructor Release Date':
          subTitle = 'The default day survey results are released to instructors for each course section.'
          break
        default:
          subTitle = 'No subtitle available'
          break
      }

      return {
        enabled: d.enabled,
        noOfDays,
        condition,
        sectionFieldType: d.sectionFieldType,
        surveyDateType: d.surveyDateType,
        subTitle
      }
    })

    const mergedData = defaultAllSurveyDates.map((defaultItem) => {
      const itemFromBackend = mappedData?.find(d => d.surveyDateType === defaultItem.surveyDateType)
      return itemFromBackend ?? defaultItem
    })

    if (!isApplyingLatestDates) {
      setValue('surveyDatesFilters', mergedData ?? [])
    } else {
      setValue('surveyDatesFilters', mergedData ?? [], { shouldDirty: true })
    }
  }, [surveyDatesList, setValue])
  // Read-only is driven by topbar status/permission rules.
  const isButtonDisabled = isReadOnly

  const handleLatestSurveyDatesClicked = (): void => {
    if (latestDefaultDates?.length > 0) {
      setIsApplyingLatestDates(true)
      setSurveyDatesList(latestDefaultDates.map(x => ({ ...x })))
      notification('Latest project sync dates settings applied.', { variant: 'success' })
    }
  }

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {isGradePostingReleaseEnabled && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Results Release Date does not set instructor release on synced sections. Only an enabled
              instructor-release rule below or a manual section date sets instructor release.
            </Alert>
          )}
          <Box minHeight={theme.spacing(112.5)}>
            {
              loading
                ? <Box display="flex" justifyContent="center" alignItems="center" minHeight={theme.spacing(100)} >
                  <CircularProgress />
                </Box>
                : fields.map((item: Record<'id', string>, index: number) =>
                  (isRollingResultsEnabled && (index === 2 || index === 3))
                    ? null
                    : (
                      <FormSection
                        fields={fields}
                        key={item.id}
                        index={index}
                        control={control}
                        disabled={isButtonDisabled}
                      />
                      )
                )}
          </Box>
          <DefaultModalActions
            confirmButtonProps={{
              type: 'submit',
              text: 'Next',
              buttonType: ButtonType.Primary,
              disabled: isPending || isButtonDisabled,
              loading: isPending
            }}
            destructiveButtonProps={{
              onClick: handleCloseModal,
              disabled: isPending
            }}
            prevSettingsProps={{
              text: 'Apply Previous Course Section Dates',
              buttonType: ButtonType.Ghost,
              onClick: handleLatestSurveyDatesClicked,
              type: 'button',
              disabled: isLatestDataFetching || isButtonDisabled
            }}
          />
        </form>
      </FormProvider>
    </>
  )
}

export default SurveyDatesPanel
